import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ApiError, api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import type {
  Account,
  AdminAuditParams,
  AdminAuditResponse,
  AdminBalanceDays,
  AdminBalanceResponse,
  AdminCallDetails,
  AdminCallsParams,
  AdminCallsResponse,
  AdminCompaniesResponse,
  AdminCompanyDetails,
  AdminCompanySort,
  AdminPeriod,
  AdminStats,
  AdminVoicePatch,
  Call,
  MinutesInfo,
  TemporaryPasswordResponse,
} from '@/shared/api/types'

function search(params: Record<string, string | number | undefined>): string {
  const result = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') result.set(key, String(value))
  }
  return result.toString()
}

function withSearch(path: string, query: string): string {
  return query ? `${path}?${query}` : path
}

function dateBoundary(value: string | undefined, endOfDay: boolean): string | undefined {
  if (!value) return undefined
  const time = endOfDay ? '23:59:59.999' : '00:00:00.000'
  return new Date(`${value}T${time}`).toISOString()
}

function callsSearch(params: AdminCallsParams): string {
  return search({
    companyId: params.companyId,
    kind: params.kind,
    status: params.status,
    from: dateBoundary(params.from, false),
    to: dateBoundary(params.to, true),
    limit: params.limit,
    offset: params.offset,
  })
}

function auditSearch(params: AdminAuditParams): string {
  return search({
    companyId: params.companyId,
    limit: params.limit,
    offset: params.offset,
  })
}

export const adminApi = {
  stats: (period: AdminPeriod, signal?: AbortSignal) =>
    api.get<AdminStats>(`/admin/stats?period=${period}`, signal),
  balance: (days: AdminBalanceDays, signal?: AbortSignal) =>
    api.get<AdminBalanceResponse>(`/admin/balance?days=${days}`, signal),
  companies: (q: string, sortBy: AdminCompanySort, signal?: AbortSignal) => {
    const query = search({ q: q.trim(), sort: sortBy })
    return api.get<AdminCompaniesResponse>(withSearch('/admin/companies', query), signal)
  },
  company: (id: number, signal?: AbortSignal) =>
    api.get<AdminCompanyDetails>(`/admin/companies/${id}`, signal),
  calls: (params: AdminCallsParams, signal?: AbortSignal) => {
    const query = callsSearch(params)
    return api.get<AdminCallsResponse>(withSearch('/admin/calls', query), signal)
  },
  companyCalls: (companyId: number, params: AdminCallsParams, signal?: AbortSignal) => {
    const query = callsSearch({ ...params, companyId: undefined })
    return api.get<AdminCallsResponse>(
      withSearch(`/admin/companies/${companyId}/calls`, query),
      signal,
    )
  },
  call: (id: number, signal?: AbortSignal) =>
    api.get<AdminCallDetails>(`/admin/calls/${id}`, signal),
  callAudioUrl: (id: number) => `/api/admin/calls/${id}/audio`,
  audit: (params: AdminAuditParams, signal?: AbortSignal) => {
    const query = auditSearch(params)
    return api.get<AdminAuditResponse>(withSearch('/admin/audit', query), signal)
  },
  renameCompany: (id: number, name: string) =>
    api.put<void>(`/admin/companies/${id}`, { name }),
  setCompanyIntegrations: (id: number, enabled: boolean) =>
    api.post<void>(`/admin/companies/${id}/integrations/${enabled ? 'on' : 'off'}`),
  setCompanyBlocked: (id: number, blocked: boolean) =>
    api.post<void>(`/admin/companies/${id}/${blocked ? 'block' : 'unblock'}`),
  updateVoice: (id: number, patch: AdminVoicePatch) =>
    api.put<void>(`/admin/companies/${id}/voice`, patch),
  resetPassword: (userId: number) =>
    api.post<TemporaryPasswordResponse>(`/admin/users/${userId}/reset-password`),
  controlCampaign: (campaignId: number, action: 'pause' | 'stop') =>
    api.post<void>(`/admin/campaigns/${campaignId}/${action}`),
  minutes: (companyId: number, signal?: AbortSignal) =>
    api.get<MinutesInfo>(`/admin/companies/${companyId}/minutes`, signal),
  addMinutes: (companyId: number, minutes: number, note: string) =>
    api.post<MinutesInfo>(`/admin/companies/${companyId}/minutes`, { minutes, note }),
  setTestMinutes: (companyId: number, testMinutesDaily: number) =>
    api.put<MinutesInfo>(`/admin/companies/${companyId}/test-minutes`, { testMinutesDaily }),
  refundCall: (callId: number, reason: string) =>
    api.post<Call>(`/admin/calls/${callId}/refund`, { reason }),
  deleteCall: (callId: number) => api.delete<void>(`/admin/calls/${callId}`),
  impersonate: (userId: number) => api.post<Account>('/admin/impersonate', { userId }),
  stopImpersonation: () => api.post<Account>('/admin/impersonation/stop'),
}

export function useAdminStats(period: AdminPeriod) {
  return useQuery({
    queryKey: queryKeys.admin.stats(period),
    queryFn: ({ signal }) => adminApi.stats(period, signal),
    staleTime: 30_000,
  })
}

export function useAdminBalance(days: AdminBalanceDays) {
  return useQuery({
    queryKey: queryKeys.admin.balance(days),
    queryFn: ({ signal }) => adminApi.balance(days, signal),
    staleTime: 30_000,
  })
}

export function useAdminCompanies(q: string, sortBy: AdminCompanySort) {
  const query = search({ q: q.trim(), sort: sortBy })
  return useQuery({
    queryKey: queryKeys.admin.companies(query),
    queryFn: ({ signal }) => adminApi.companies(q, sortBy, signal),
    placeholderData: (previous) => previous,
  })
}

export function useAdminCompany(id: number | null) {
  return useQuery({
    queryKey: queryKeys.admin.company(id),
    queryFn: ({ signal }) => adminApi.company(id as number, signal),
    enabled: id !== null,
  })
}

export function useAdminCalls(params: AdminCallsParams) {
  const query = callsSearch(params)
  return useQuery({
    queryKey: queryKeys.admin.calls('all', query),
    queryFn: ({ signal }) => adminApi.calls(params, signal),
    placeholderData: (previous) => previous,
  })
}

export function useAdminCompanyCalls(companyId: number | null, params: AdminCallsParams) {
  const query = callsSearch({ ...params, companyId: undefined })
  return useQuery({
    queryKey: queryKeys.admin.calls(`company-${companyId ?? 'none'}`, query),
    queryFn: ({ signal }) => adminApi.companyCalls(companyId as number, params, signal),
    enabled: companyId !== null,
    placeholderData: (previous) => previous,
  })
}

export function useAdminCall(id: number | null) {
  return useQuery({
    queryKey: queryKeys.admin.call(id),
    queryFn: ({ signal }) => adminApi.call(id as number, signal),
    enabled: id !== null,
  })
}

export function useAdminAudit(params: AdminAuditParams) {
  const query = auditSearch(params)
  return useQuery({
    queryKey: queryKeys.admin.audit(query),
    queryFn: ({ signal }) => adminApi.audit(params, signal),
    placeholderData: (previous) => previous,
  })
}

function showMutationError(error: unknown, fallback: string) {
  toast.error(error instanceof ApiError ? error.message : fallback)
}

export function useAdminRenameCompany(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => adminApi.renameCompany(companyId, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success('Название компании изменено')
    },
    onError: (error) => showMutationError(error, 'Не удалось переименовать компанию'),
  })
}

export function useAdminSetCompanyBlocked(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (blocked: boolean) => adminApi.setCompanyBlocked(companyId, blocked),
    onSuccess: (_data, blocked) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success(blocked ? 'Компания заблокирована' : 'Компания разблокирована')
    },
    onError: (error) => showMutationError(error, 'Не удалось изменить доступ компании'),
  })
}

export function useAdminSetCompanyIntegrations(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (enabled: boolean) => adminApi.setCompanyIntegrations(companyId, enabled),
    onSuccess: (_data, enabled) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success(enabled ? 'Интеграции включены' : 'Интеграции выключены')
    },
    onError: (error) => showMutationError(error, 'Не удалось изменить доступ к интеграциям'),
  })
}

export function useAdminUpdateVoice(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: AdminVoicePatch) => adminApi.updateVoice(companyId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success('Настройки голоса сохранены')
    },
    onError: (error) => showMutationError(error, 'Не удалось сохранить настройки голоса'),
  })
}

export function useAdminResetPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => adminApi.resetPassword(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success('Временный пароль создан')
    },
    onError: (error) => showMutationError(error, 'Не удалось сбросить пароль'),
  })
}

export function useAdminCampaignAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ campaignId, action }: { campaignId: number; action: 'pause' | 'stop' }) =>
      adminApi.controlCampaign(campaignId, action),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success(variables.action === 'pause' ? 'Обзвон приостановлен' : 'Обзвон остановлен')
    },
    onError: (error) => showMutationError(error, 'Не удалось изменить обзвон'),
  })
}

export function useAdminImpersonate() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (userId: number) => adminApi.impersonate(userId),
    onSuccess: async (account) => {
      // Не даём старому /api/me завершиться после смены cookie и вернуть
      // админский аккаунт поверх impersonation-сессии.
      await queryClient.cancelQueries({ queryKey: queryKeys.account })
      // Сначала уходим из RequireAdmin: новая сессия уже не суперадминская.
      // Затем выбрасываем данные прежней компании, чтобы они не мигнули у клиента.
      navigate('/', { replace: true, flushSync: true })
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== queryKeys.account[0] })
      queryClient.setQueryData(queryKeys.account, account)
    },
    onError: (error) => showMutationError(error, 'Не удалось войти в кабинет клиента'),
  })
}

export function useAdminStopImpersonation() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => adminApi.stopImpersonation(),
    onSuccess: async (account) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.account })
      // Возвращаем админский аккаунт до входа под RequireAdmin и очищаем
      // клиентский кэш, который принадлежал компании impersonation-сессии.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== queryKeys.account[0] })
      queryClient.setQueryData(queryKeys.account, account)
      navigate('/admin', { replace: true, flushSync: true })
    },
    onError: (error) => showMutationError(error, 'Не удалось вернуться в админку'),
  })
}

export function useAdminMinutes(companyId: number) {
  return useQuery({
    queryKey: queryKeys.admin.minutes(companyId),
    queryFn: ({ signal }) => adminApi.minutes(companyId, signal),
  })
}

export function useAdminAddMinutes(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ minutes, note }: { minutes: number; note: string }) =>
      adminApi.addMinutes(companyId, minutes, note),
    onSuccess: (data, { minutes }) => {
      queryClient.setQueryData(queryKeys.admin.minutes(companyId), data)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success(minutes > 0 ? `Выдано ${minutes} мин` : `Снято ${-minutes} мин`)
    },
    onError: (error) => showMutationError(error, 'Не удалось изменить пакет минут'),
  })
}

export function useAdminSetTestMinutes(companyId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (minutes: number) => adminApi.setTestMinutes(companyId, minutes),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.admin.minutes(companyId), data)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success('Лимит тестовых звонков сохранён')
    },
    onError: (error) => showMutationError(error, 'Не удалось сохранить лимит'),
  })
}

/** Удаление звонка (тестового, ошибочного) вместе с записью. */
export function useAdminDeleteCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (callId: number) => adminApi.deleteCall(callId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      // Клиентские списки и статистика тоже кэшируются — сбросить всё.
      void queryClient.invalidateQueries()
      toast.success('Звонок удалён')
    },
    onError: (error) => showMutationError(error, 'Не удалось удалить звонок'),
  })
}

export function useAdminRefundCall(callId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => adminApi.refundCall(callId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.all })
      toast.success('Минуты за звонок возвращены')
    },
    onError: (error) => showMutationError(error, 'Не удалось вернуть минуты'),
  })
}
